import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isApiError } from '@/lib/api';
import styles from './Toast.module.css';
import { ToastContext, type Toast, type ToastApi } from './toast-context';

const ICONS = { success: CheckCircle2, info: Info, error: TriangleAlert };

/** Toast region (bottom right). Use `useToast().show(...)` to add one. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t: translate } = useTranslation();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  /** Auto-dismiss timers, held while a toast is hovered or focused. */
  const timers = useRef(
    new Map<
      number,
      {
        handle: ReturnType<typeof setTimeout> | null;
        left: number;
        since: number;
      }
    >(),
  );

  const dismiss = useCallback((id: number) => {
    const tm = timers.current.get(id);
    if (tm?.handle) clearTimeout(tm.handle);
    timers.current.delete(id);
    setToasts((all) => all.filter((t) => t.id !== id));
  }, []);

  const arm = useCallback(
    (id: number, ms: number) => {
      timers.current.set(id, {
        handle: setTimeout(() => dismiss(id), ms),
        left: ms,
        since: Date.now(),
      });
    },
    [dismiss],
  );

  const show = useCallback<ToastApi['show']>(
    (toast, durationMs = 5000) => {
      const id = nextId.current++;
      setToasts((all) => [...all.slice(-3), { ...toast, id }]);
      if (durationMs > 0) arm(id, durationMs);
    },
    [arm],
  );

  /** RL-1658 review: hold the toast (and its Undo) while it has attention. */
  const hold = useCallback((t: Toast) => {
    const tm = timers.current.get(t.id);
    if (tm?.handle) {
      clearTimeout(tm.handle);
      tm.handle = null;
      tm.left = Math.max(0, tm.left - (Date.now() - tm.since));
    }
    t.onPause?.();
  }, []);
  const release = useCallback(
    (t: Toast) => {
      const tm = timers.current.get(t.id);
      if (tm && !tm.handle) arm(t.id, tm.left);
      t.onResume?.();
    },
    [arm],
  );

  const api = useMemo<ToastApi>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.region} aria-live="polite" aria-atomic="false">
        {toasts.map((t) => {
          const Icon = ICONS[t.tone];
          const apiError = isApiError(t.error) ? t.error : undefined;
          const retry = t.onRetry ?? (() => window.location.reload());
          return (
            <div
              key={t.id}
              className={[styles.toast, styles[t.tone]].join(' ')}
              role={t.tone === 'error' ? 'alert' : 'status'}
              onMouseEnter={() => hold(t)}
              onMouseLeave={() => release(t)}
              onFocus={() => hold(t)}
              onBlur={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null))
                  release(t);
              }}
            >
              <Icon size={18} aria-hidden="true" />
              <div className={styles.text}>
                <p className={styles.title}>{t.title}</p>
                {(t.body || apiError?.message) && (
                  <p className={styles.body}>{t.body ?? apiError?.message}</p>
                )}
                {/* RL-1644: the code, reference and retry belong to a failed
                    request; a plain refusal message stands alone. */}
                {t.tone === 'error' && t.error !== undefined && (
                  <>
                    <p className={styles.code}>
                      {translate('common.problemCode', {
                        code: apiError?.code ?? translate('errors.unknownCode'),
                      })}
                    </p>
                    {apiError?.requestId && (
                      <p className={styles.code}>
                        {translate('common.requestId', {
                          id: apiError.requestId,
                        })}
                      </p>
                    )}
                    <button
                      type="button"
                      className={styles.retry}
                      onClick={() => {
                        dismiss(t.id);
                        retry();
                      }}
                    >
                      {translate('common.retry')}
                    </button>
                  </>
                )}
              </div>
              {t.action && (
                <button
                  type="button"
                  className={styles.action}
                  onClick={() => {
                    dismiss(t.id);
                    t.action?.onClick();
                  }}
                >
                  {t.action.label}
                </button>
              )}
              <button
                type="button"
                className={styles.close}
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
